export const LIMIT_FIELDS = [
    { key: 'max_tenders_per_day', label: 'Tenders per day', unlimitedLabel: 'Unlimited', placeholder: 'e.g. 30', required: true,
      hint: 'Counts tender records returned, not requests.' },
    { key: 'max_tenders_per_month', label: 'Tenders per month', unlimitedLabel: 'Unlimited', placeholder: 'e.g. 600', required: true,
      hint: 'Monthly cap on tender records returned.' },
    { key: 'max_requests_per_day', label: 'Requests per day', unlimitedLabel: 'Unlimited', placeholder: 'e.g. 500', required: true,
      hint: 'API calls per day, whatever they return.' },
    { key: 'rate_limit_per_min', label: 'Requests per minute', unlimitedLabel: 'Unlimited', placeholder: 'e.g. 30', required: true,
      hint: 'Burst control.' },
    { key: 'max_page_size', label: 'Max page size', unlimitedLabel: 'Server default (100)', placeholder: 'e.g. 50', required: false,
      hint: 'Largest `limit` allowed in one call.' },
    { key: 'history_days', label: 'History window (days)', unlimitedLabel: 'All history', placeholder: 'e.g. 90', required: false,
      hint: 'How far back tenders are visible.' },
];

export const FEATURE_FLAGS = [
    { key: 'documents', label: 'Document links' },
    { key: 'authority_contact', label: 'Authority contact details' },
    { key: 'updated_since', label: 'Incremental sync (updated_since)' },
    { key: 'signed_mode_required', label: 'Require signed requests' },
];

export function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` };
}

export function errorMessage(err, fallback) {
    const d = err.response?.data;
    if (d?.errors && typeof d.errors === 'object') {
        const first = Object.entries(d.errors)[0];
        if (first) return `${d.message || fallback}: ${first[0]} - ${first[1]}`;
    }
    return d?.message || d?.detail || fallback;
}

export function makePlanState(plan) {
    const limits = {};
    LIMIT_FIELDS.forEach(f => {
        if (!plan) {
            limits[f.key] = { mode: f.required ? 'custom' : 'unlimited', value: '' };
        } else {
            const v = plan[f.key];
            limits[f.key] = v == null ? { mode: 'unlimited', value: '' } : { mode: 'custom', value: v };
        }
    });
    const ap = plan ? plan.allowed_portals : null;
    const features = {};
    FEATURE_FLAGS.forEach(f => { features[f.key] = plan?.features?.[f.key] ? 'on' : 'off'; });
    return {
        limits,
        allowed_portals: ap == null ? { mode: 'unlimited', value: [] } : { mode: 'custom', value: [...ap] },
        features,
    };
}

export function makeOverrideState(overrides) {
    const o = overrides || {};
    const limits = {};
    LIMIT_FIELDS.forEach(f => {
        if (!(f.key in o)) limits[f.key] = { mode: 'inherit', value: '' };
        else limits[f.key] = o[f.key] == null ? { mode: 'unlimited', value: '' } : { mode: 'custom', value: o[f.key] };
    });
    let allowed = { mode: 'inherit', value: [] };
    if ('allowed_portals' in o) {
        allowed = o.allowed_portals == null ? { mode: 'unlimited', value: [] } : { mode: 'custom', value: [...o.allowed_portals] };
    }
    const features = {};
    FEATURE_FLAGS.forEach(f => {
        const of = o.features || {};
        features[f.key] = f.key in of ? (of[f.key] ? 'on' : 'off') : 'inherit';
    });
    return { limits, allowed_portals: allowed, features };
}

export function planPayload(state) {
    const out = {};
    LIMIT_FIELDS.forEach(f => {
        const s = state.limits[f.key];
        out[f.key] = s.mode === 'unlimited' ? null : Number(s.value);
    });
    out.allowed_portals = state.allowed_portals.mode === 'unlimited' ? null : [...state.allowed_portals.value];
    const features = {};
    FEATURE_FLAGS.forEach(f => { features[f.key] = state.features[f.key] === 'on'; });
    out.features = features;
    return out;
}

export function overridePayload(state) {
    const out = {};
    LIMIT_FIELDS.forEach(f => {
        const s = state.limits[f.key];
        if (s.mode === 'unlimited') out[f.key] = null;
        else if (s.mode === 'custom') out[f.key] = Number(s.value);
    });
    const ap = state.allowed_portals;
    if (ap.mode === 'unlimited') out.allowed_portals = null;
    else if (ap.mode === 'custom') out.allowed_portals = [...ap.value];
    const features = {};
    FEATURE_FLAGS.forEach(f => {
        const v = state.features[f.key];
        if (v === 'on') features[f.key] = true;
        else if (v === 'off') features[f.key] = false;
    });
    if (Object.keys(features).length) out.features = features;
    return out;
}

export function validateState(state) {
    for (const f of LIMIT_FIELDS) {
        const s = state.limits[f.key];
        if (s.mode === 'custom') {
            const n = Number(s.value);
            if (s.value === '' || !Number.isInteger(n) || n < 1) {
                return `${f.label}: enter a whole number of at least 1, or choose ${f.unlimitedLabel}.`;
            }
        }
    }
    if (state.allowed_portals.mode === 'custom' && !state.allowed_portals.value.length) {
        return 'Allowed portals: select at least one portal, or choose All portals.';
    }
    return '';
}

export function parseIpList(text) {
    return (text || '').split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
}

export function invalidIps(list) {
    return list.filter(ip => !/^[0-9a-fA-F:.]+(\/\d{1,3})?$/.test(ip));
}

export function fmtLimit(v, unlimitedLabel = 'Unlimited') {
    return v == null ? unlimitedLabel : Number(v).toLocaleString('en-IN');
}

export function fmtDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function describeOverrides(overrides) {
    const o = overrides || {};
    const out = [];
    LIMIT_FIELDS.forEach(f => {
        if (f.key in o) out.push(`${f.label}: ${o[f.key] == null ? f.unlimitedLabel : Number(o[f.key]).toLocaleString('en-IN')}`);
    });
    if ('allowed_portals' in o) {
        out.push(`Portals: ${o.allowed_portals == null ? 'All portals' : (o.allowed_portals.join(', ') || 'none')}`);
    }
    FEATURE_FLAGS.forEach(f => {
        if (o.features && f.key in o.features) out.push(`${f.label}: ${o.features[f.key] ? 'on' : 'off'}`);
    });
    return out;
}

export async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (e2) { ok = false; }
        document.body.removeChild(ta);
        return ok;
    }
}
